import type { ReactNode } from 'react';
import { cn } from '@up2d8/ui';

/**
 * Tabel, persoon in een cel, percentage en kerncijfers (spec A §5 "Tabel" t/m "Kerncijfers").
 *
 * De tabel zelf schrijft het scherm als gewone `<thead>`/`<tbody>`: kolommen verschillen te veel
 * per scherm om ze in props te vangen. Klassen voor cellen: `num` (rechts, tabelcijfers) en `act`
 * (acties, rechts, niet afbreken); een geselecteerde rij krijgt `sel`.
 */

/** `.a-table-wrap` + `.a-table`. `label` wordt het `aria-label` van de tabel. */
export function Table({
  label,
  testId,
  children,
}: {
  label?: string;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <div className="a-table-wrap">
      <table className="a-table" aria-label={label} data-testid={testId}>
        {children}
      </table>
    </div>
  );
}

/** De laatste cel met acties: rechts, niet afbreken, knoppen naast elkaar. */
export function ActionsCell({ children }: { children: ReactNode }) {
  return (
    <td className="act">
      <div className="a-actions" style={{ justifyContent: 'flex-end' }}>
        {children}
      </div>
    </td>
  );
}

export type AvatarTone = 'a' | 'b' | 'c' | 'd' | 'e' | 'f';

/** Twee letters uit een naam: "Sarah De Backer" wordt "SD". */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '';
  const second = parts.length > 1 ? (parts[1]?.[0] ?? '') : (parts[0]?.[1] ?? '');
  return `${first}${second}`.toUpperCase();
}

/** Een vaste kleur per naam uit de zes van de mockup (`.avatar-a` t/m `-f`). */
export function avatarTone(name: string): AvatarTone {
  const tones: AvatarTone[] = ['a', 'b', 'c', 'd', 'e', 'f'];
  let sum = 0;
  for (const char of name) sum = (sum + char.charCodeAt(0)) % 997;
  return tones[sum % tones.length] as AvatarTone;
}

/** `.avatar`: initialen op een verloop, of een foto. Altijd `aria-hidden`: de naam staat ernaast. */
export function Avatar({
  name,
  photoUrl,
  size = 'md',
  className,
}: {
  name: string;
  photoUrl?: string | null;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  return (
    <div
      className={cn('avatar', `avatar-${size}`, `avatar-${avatarTone(name)}`, className)}
      aria-hidden="true"
    >
      {photoUrl ? (
        <img src={photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        initials(name)
      )}
    </div>
  );
}

/** `.a-person`: avatar 32 + naam (Poppins 600, 14 px) + metaregel van 12 px. */
export function PersonCell({
  name,
  meta,
  photoUrl,
}: {
  name: string;
  meta?: ReactNode;
  photoUrl?: string | null;
}) {
  return (
    <div className="a-person">
      <Avatar name={name} photoUrl={photoUrl} />
      <div style={{ minWidth: 0 }}>
        <div className="a-name">{name}</div>
        {meta ? <div className="a-meta">{meta}</div> : null}
      </div>
    </div>
  );
}

/**
 * Percentage (`.a-pct`): balk van 80 × 6, vulling `#007A99`, met de tekst ernaast ("63%" of
 * "18/31 · 58%"). De balk is versiering; de tekst zegt het.
 */
export function Percent({ value, label }: { value: number; label?: ReactNode }) {
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div className="a-pct">
      <div className="a-bar" style={{ width: 80 }} aria-hidden="true">
        <span style={{ width: `${clamped}%` }} />
      </div>
      {label ?? `${Math.round(clamped)}%`}
    </div>
  );
}

export interface StatItem {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  testId?: string;
}

/** `.a-stats`: drie tegels naast elkaar; label 11 px hoofdletters, getal Poppins 700, 28 px. */
export function Stats({ items, label }: { items: StatItem[]; label?: string }) {
  return (
    <dl className="a-stats" aria-label={label}>
      {items.map((item, index) => (
        <div key={index} className="a-stat">
          <dt className="a-stat-label">{item.label}</dt>
          <dd className="a-stat-num" data-testid={item.testId}>
            {item.value}
          </dd>
          {item.sub ? <dd className="a-stat-sub">{item.sub}</dd> : null}
        </div>
      ))}
    </dl>
  );
}
